/**
 * A CAMPAIGN PAGE, FETCHED RATHER THAN BUNDLED.
 *
 * WHY THIS IS NOT IN THE BUNDLE ANY MORE. A page's copy used to live in `$lib/themes`, so pairing
 * a page with a capability was two changes in two places -- the words in the frontend, the coach
 * in the database -- and "this page is about apologies and starts the apology coach" could not be
 * done without a release. The page is a row now, and this module fetches it.
 *
 * THE BUNDLED COPY IS STILL HERE, AND ONLY AS AN OFFLINE FALLBACK. A visitor who arrives on an
 * advertisement must never see a spinner where the promise should be, so the bundled theme renders
 * immediately and the server's copy replaces it when it arrives. If the API never answers, the
 * page is still a coherent page.
 *
 * WHAT IS NOT FALLBACK-ABLE, AND WHY. A page the SERVER does not know gets no bundled
 * substitute: a mistyped advertisement URL (`/l/refrased`) would otherwise quietly sell the
 * product with the DEFAULT page's copy, which reads as a working page and is the wrong promise.
 * An unreachable API is a different situation from a page that does not exist, and the two get
 * different answers: fall back only for the first.
 */
import { writable } from 'svelte/store';

import { WEBUI_BASE_URL } from '$lib/constants';
import { resolveTheme, type LandingTheme } from '$lib/themes';

export interface ServedPage {
	id: string;
	headline: string;
	subhead: string;
	pillars: string[];
	meta_description: string;
	/** The guided paths to lead with, most relevant first. */
	chips_order: string[];
	/** Which capability this page starts, resolved by the server from the routing rules. */
	capability: string | null;
}

/** Pages the server has answered with, keyed by id, so a revisit costs nothing. */
export const servedPages = writable<Record<string, ServedPage>>({});

/**
 * Pages the server EXPLICITLY said do not exist.
 *
 * THE DISTINCTION IS THE WHOLE POINT OF THIS STORE. "The server has not answered yet" and "the
 * server says there is no such page" are different facts, and treating them the same is how a
 * mistyped advertisement URL (`/l/sorrry`) ends up quietly selling the product with the DEFAULT
 * page's copy. A visitor who followed a broken link should be told the page is not there, not
 * handed somebody else's promise.
 */
export const missingPages = writable<Record<string, true>>({});

const cache: Record<string, ServedPage> = {};

/**
 * The page to render, and whether the server has answered for it yet.
 *
 * `served` is null while the request is in flight and stays null if the API is unreachable, in
 * which case the bundled theme is what the visitor reads. The bundled copy is never used to
 * answer a page the server has explicitly denied -- that is a 404 on the server and the route
 * handles it.
 */
export const pageFor = (
	id: string,
	served: Record<string, ServedPage>
): { theme: LandingTheme; page: ServedPage | null } => {
	const theme = resolveTheme(id);
	return { theme, page: served[id] ?? null };
};

/**
 * Fetch one page's copy. Returns null when the server does not have it or cannot be reached.
 *
 * The two are deliberately NOT distinguished here: both mean "render what you have". The
 * difference matters for a page the server denies, and the caller learns that from the returned
 * `known` flag on the store rather than from this function's failure mode.
 */
export const loadPage = async (id: string): Promise<ServedPage | null> => {
	if (cache[id]) return cache[id];
	try {
		const response = await fetch(`${WEBUI_BASE_URL}/api/v1/landings/${encodeURIComponent(id)}`, {
			credentials: 'include'
		});
		if (response.status === 404) {
			// THE SERVER SAYS THIS PAGE DOES NOT EXIST. That is an answer, not a failure, and it is
			// recorded so the route can say so rather than substituting a different page.
			missingPages.update((current) => ({ ...current, [id]: true }));
			return null;
		}
		if (!response.ok) return null;
		const page: ServedPage = await response.json();
		if (!page?.id || !page.headline) return null;
		cache[id] = page;
		servedPages.update((current) => ({ ...current, [page.id]: page }));
		return page;
	} catch {
		return null;
	}
};

/**
 * The guided-path order for this page: the server's when it has one, the bundle's otherwise.
 *
 * Both are only an ORDER -- a chip neither mentions is still offered, after the ones they do.
 */
export const chipOrderFor = (theme: LandingTheme, page: ServedPage | null): string[] =>
	page?.chips_order?.length ? page.chips_order : theme.scenarioIds;
